<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DatePicker from 'vue-datepicker-next';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  WEEKDAY_CODES,
  buildRule,
  parseRule,
  presetFor,
  presetRule,
  weekdayCode,
  weekdayName,
} from 'dashboard/helper/taskRecurrence';

const props = defineProps({
  modelValue: { type: String, default: null },
  dueDate: { type: Date, default: null },
});

const emit = defineEmits(['update:modelValue']);

const { t, locale } = useI18n();

const intlLocale = computed(() => locale.value.replace('_', '-'));
const referenceDate = computed(() => props.dueDate || new Date());

const preset = ref(presetFor(props.modelValue, referenceDate.value));

const custom = reactive({
  freq: 'WEEKLY',
  interval: 1,
  byDay: [weekdayCode(referenceDate.value)],
  endType: 'never',
  until: null,
  count: 1,
});

const loadCustomFromRule = rule => {
  const parsed = parseRule(rule);
  if (!parsed) return;
  Object.assign(custom, {
    ...parsed,
    byDay: parsed.byDay.length
      ? parsed.byDay
      : [weekdayCode(referenceDate.value)],
    count: parsed.count || 1,
  });
};

if (preset.value === 'custom') loadCustomFromRule(props.modelValue);

const presetOptions = computed(() => {
  const date = referenceDate.value;
  const weekday = weekdayName(weekdayCode(date), intlLocale.value);
  const dateLabel = new Intl.DateTimeFormat(intlLocale.value, {
    day: 'numeric',
    month: 'long',
  }).format(date);
  return [
    { value: 'none', label: t('TASKS.RECURRENCE.PRESETS.NONE') },
    { value: 'daily', label: t('TASKS.RECURRENCE.PRESETS.DAILY') },
    {
      value: 'weekly',
      label: t('TASKS.RECURRENCE.PRESETS.WEEKLY', { weekday }),
    },
    {
      value: 'monthly',
      label: t('TASKS.RECURRENCE.PRESETS.MONTHLY', { day: date.getDate() }),
    },
    {
      value: 'yearly',
      label: t('TASKS.RECURRENCE.PRESETS.YEARLY', { date: dateLabel }),
    },
    { value: 'weekdays', label: t('TASKS.RECURRENCE.PRESETS.WEEKDAYS') },
    { value: 'custom', label: t('TASKS.RECURRENCE.PRESETS.CUSTOM') },
  ];
});

const unitOptions = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'].map(value => ({
  value,
  label: t(`TASKS.RECURRENCE.UNITS.${value}`),
}));

const weekdays = computed(() =>
  WEEKDAY_CODES.map(code => ({
    code,
    label: weekdayName(code, intlLocale.value).charAt(0).toUpperCase(),
    name: weekdayName(code, intlLocale.value),
  }))
);

const toggleWeekday = code => {
  const selected = custom.byDay.includes(code);
  if (selected && custom.byDay.length === 1) return;
  custom.byDay = selected
    ? custom.byDay.filter(day => day !== code)
    : [...custom.byDay, code];
};

const rule = computed(() => {
  if (preset.value !== 'custom')
    return presetRule(preset.value, referenceDate.value);
  return buildRule({
    ...custom,
    interval: Math.max(1, Number(custom.interval) || 1),
    count: Math.max(1, Number(custom.count) || 1),
  });
});

watch(
  rule,
  value => {
    if (value !== props.modelValue) emit('update:modelValue', value);
  },
  { immediate: true }
);

watch(
  () => props.modelValue,
  value => {
    if (value === rule.value) return;
    preset.value = presetFor(value, referenceDate.value);
    if (preset.value === 'custom') loadCustomFromRule(value);
  }
);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-col gap-1">
      <span class="mb-0.5 text-heading-3 text-n-slate-12">
        {{ t('TASKS.RECURRENCE.LABEL') }}
      </span>
      <ComboBox v-model="preset" :options="presetOptions" />
    </div>

    <div
      v-if="preset === 'custom'"
      class="flex flex-col gap-4 p-4 rounded-xl border border-n-weak"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('TASKS.RECURRENCE.CUSTOM_TITLE') }}
      </span>

      <div class="flex items-end gap-2">
        <div class="flex flex-col gap-1 w-24">
          <span class="text-sm text-n-slate-11">
            {{ t('TASKS.RECURRENCE.EVERY') }}
          </span>
          <Input
            v-model="custom.interval"
            type="number"
            min="1"
            custom-input-class="h-8 !pt-1 !pb-1"
          />
        </div>
        <div class="w-40">
          <ComboBox v-model="custom.freq" :options="unitOptions" />
        </div>
      </div>

      <div v-if="custom.freq === 'WEEKLY'" class="flex flex-col gap-2">
        <span class="text-sm text-n-slate-11">
          {{ t('TASKS.RECURRENCE.REPEAT_ON') }}
        </span>
        <div class="flex gap-1">
          <button
            v-for="day in weekdays"
            :key="day.code"
            type="button"
            :title="day.name"
            class="flex items-center justify-center rounded-full size-8 text-sm"
            :class="
              custom.byDay.includes(day.code)
                ? 'bg-n-brand text-white'
                : 'bg-n-alpha-2 text-n-slate-11'
            "
            @click="toggleWeekday(day.code)"
          >
            {{ day.label }}
          </button>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm text-n-slate-11">
          {{ t('TASKS.RECURRENCE.ENDS') }}
        </span>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="custom.endType" type="radio" value="never" />
          {{ t('TASKS.RECURRENCE.NEVER') }}
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="custom.endType" type="radio" value="until" />
          <span class="w-12">{{ t('TASKS.RECURRENCE.ON_DATE') }}</span>
          <DatePicker
            v-model:value="custom.until"
            type="date"
            :disabled="custom.endType !== 'until'"
            :append-to-body="false"
            input-class="mx-input"
          />
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="custom.endType" type="radio" value="count" />
          <span class="w-12">{{ t('TASKS.RECURRENCE.AFTER') }}</span>
          <Input
            v-model="custom.count"
            type="number"
            min="1"
            :disabled="custom.endType !== 'count'"
            class="w-20"
            custom-input-class="h-8 !pt-1 !pb-1"
          />
          {{ t('TASKS.RECURRENCE.OCCURRENCES') }}
        </label>
      </div>
    </div>
  </div>
</template>
